import { useState, useRef, useEffect } from "react";
import {
  Download,
  Upload,
  FileArchive,
  FileJson,
  Shield,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Clock,
  CalendarDays,
} from "lucide-react";
import { api } from "@/lib/api";

interface BackupFile {
  key: string;
  tier: "weekly" | "monthly";
  size: number;
  uploaded: string;
  sound_count: string | null;
  combo_count: string | null;
  board_count: string | null;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function AdminBackups() {
  // Identity — used to gate restore
  const [identity, setIdentity] = useState<{ email: string | null; name: string | null }>({
    email: null,
    name: null,
  });
  useEffect(() => {
    api.admin.me().then(setIdentity).catch(() => {});
  }, []);

  const isPrimary = identity.email?.toLowerCase() === "brandon@shough.io";
  const canRestore = isPrimary;

  // Export state
  const [exporting, setExporting] = useState<"json" | "zip" | null>(null);

  // Restore state
  const fileRef = useRef<HTMLInputElement>(null);
  const [restoreFile, setRestoreFile] = useState<File | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState<{
    sounds_imported: number;
    sounds_skipped: number;
    combos_imported: number;
    combos_skipped: number;
    boards_imported: number;
    boards_skipped: number;
    errors: string[];
  } | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  // Scheduled backup files from R2
  const [backupFiles, setBackupFiles] = useState<BackupFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [runningBackup, setRunningBackup] = useState(false);
  const [backupStatus, setBackupStatus] = useState<"idle" | "running" | "success" | "error">("idle");
  const [backupError, setBackupError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  const loadBackupFiles = () => {
    api.admin
      .backupsList()
      .then((data) => setBackupFiles(data.files))
      .catch(() => {})
      .finally(() => setLoadingFiles(false));
  };

  useEffect(() => {
    loadBackupFiles();
  }, []);

  const handleExport = async (format: "json" | "zip") => {
    setExporting(format);
    try {
      const url = format === "json" ? api.admin.backupJson() : api.admin.backupZip();
      const res = await fetch(url);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      const date = new Date().toISOString().split("T")[0];
      a.download = `wizard-sounds-backup-${date}.${format === "json" ? "json" : "zip"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      const url = format === "json" ? api.admin.backupJson() : api.admin.backupZip();
      window.location.href = url;
    } finally {
      setExporting(null);
    }
  };

  const handleRestore = async () => {
    if (!restoreFile || !canRestore) return;
    setRestoring(true);
    setRestoreResult(null);
    setRestoreError(null);

    try {
      const result = await api.admin.restore(restoreFile);
      setRestoreResult(result);
      setRestoreFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      setRestoreError(err instanceof Error ? err.message : "Restore failed");
    } finally {
      setRestoring(false);
    }
  };

  const handleRunBackup = async () => {
    setRunningBackup(true);
    setBackupStatus("running");
    setBackupError(null);
    try {
      await api.admin.maintenance.runJob("scheduled-backup");
      // Refresh file list to show the new backup
      const data = await api.admin.backupsList();
      setBackupFiles(data.files);
      setBackupStatus("success");
    } catch (err) {
      setBackupStatus("error");
      setBackupError(err instanceof Error ? err.message : "Backup failed");
    } finally {
      setRunningBackup(false);
    }
  };

  const handleDownloadBackup = async (file: BackupFile) => {
    setDownloading(file.key);
    try {
      const url = api.admin.backupDownloadUrl(file.key);
      const res = await fetch(url);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = file.key.split("/").pop() ?? "backup.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      const url = api.admin.backupDownloadUrl(file.key);
      window.location.href = url;
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div>
      <h1 className="font-display text-2xl font-bold tracking-display">Backups</h1>

      <div className="mt-6 space-y-6">
        {/* ── Manual Export ── */}
        <div className="rounded-[18px] border border-mist bg-slate p-5">
          <h2 className="flex items-center gap-2 text-lg font-medium text-parchment">
            <Download className="h-5 w-5 text-spark" />
            Export Backup
          </h2>
          <p className="mt-1 text-sm text-fog">
            Download a full backup of everything — sounds, tags, combos, boards, webhooks, and audio
            files.
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={() => handleExport("zip")}
              disabled={exporting !== null}
              className="flex items-center gap-2 rounded-xl bg-spark px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-spark-soft disabled:opacity-50"
            >
              {exporting === "zip" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileArchive className="h-4 w-4" />
              )}
              {exporting === "zip" ? "Packaging..." : "Full Backup (ZIP)"}
            </button>
            <button
              onClick={() => handleExport("json")}
              disabled={exporting !== null}
              className="flex items-center gap-2 rounded-xl border border-mist px-4 py-2.5 text-sm font-medium text-parchment transition-colors hover:border-spark hover:text-spark disabled:opacity-50"
            >
              {exporting === "json" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileJson className="h-4 w-4" />
              )}
              {exporting === "json" ? "Exporting..." : "Metadata Only (JSON)"}
            </button>
          </div>
          <p className="mt-2 text-xs text-fog">
            ZIP includes all audio files + metadata. JSON is metadata only (sounds, tags, combos, boards).
          </p>
        </div>

        {/* ── Restore ── */}
        <div className="rounded-[18px] border border-mist bg-slate p-5">
          <h2 className="flex items-center gap-2 text-lg font-medium text-parchment">
            <Upload className="h-5 w-5 text-spark" />
            Restore from Backup
          </h2>
          <p className="mt-1 text-sm text-fog">
            Upload a previous backup file to restore sounds, combos, and boards. Existing items are
            skipped.
          </p>

          {!canRestore ? (
            <div className="mt-4 flex items-center gap-3 rounded-xl border border-mist bg-ink/50 px-4 py-3">
              <Shield className="h-5 w-5 shrink-0 text-fog" />
              <div>
                <p className="text-sm font-medium text-parchment">Restore is restricted</p>
                <p className="mt-0.5 text-xs text-fog">
                  Only the primary admin (brandon@shough.io) can restore backups. Contact them if
                  you need data restored.
                </p>
              </div>
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              <div className="flex items-center gap-3">
                <input
                  ref={fileRef}
                  type="file"
                  accept=".zip,.json"
                  onChange={(e) => {
                    setRestoreFile(e.target.files?.[0] ?? null);
                    setRestoreResult(null);
                    setRestoreError(null);
                  }}
                  className="block w-full text-sm text-fog file:mr-3 file:rounded-xl file:border-0 file:bg-slate-raised file:px-4 file:py-2 file:text-sm file:font-medium file:text-parchment file:transition-colors hover:file:bg-mist"
                />
                <button
                  onClick={handleRestore}
                  disabled={!restoreFile || restoring}
                  className="flex shrink-0 items-center gap-2 rounded-xl bg-spark px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-spark-soft disabled:opacity-50"
                >
                  {restoring ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  {restoring ? "Restoring..." : "Restore"}
                </button>
              </div>

              {restoreFile && !restoring && !restoreResult && (
                <p className="text-xs text-fog">
                  Ready to restore from{" "}
                  <span className="font-medium text-parchment">{restoreFile.name}</span>{" "}
                  ({(restoreFile.size / 1024 / 1024).toFixed(1)} MB). Existing items will be
                  skipped.
                </p>
              )}

              {restoreError && (
                <div className="flex items-center gap-2 rounded-xl bg-ember/10 px-4 py-3 text-sm text-ember">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  {restoreError}
                </div>
              )}

              {restoreResult && (
                <div className="rounded-xl border border-moss/30 bg-moss/5 p-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-moss">
                    <CheckCircle2 className="h-4 w-4" />
                    Restore complete
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-3">
                    {[
                      {
                        label: "Sounds",
                        imported: restoreResult.sounds_imported,
                        skipped: restoreResult.sounds_skipped,
                      },
                      {
                        label: "Combos",
                        imported: restoreResult.combos_imported,
                        skipped: restoreResult.combos_skipped,
                      },
                      {
                        label: "Boards",
                        imported: restoreResult.boards_imported,
                        skipped: restoreResult.boards_skipped,
                      },
                    ].map(({ label, imported, skipped }) => (
                      <div key={label} className="rounded-xl bg-ink/50 px-3 py-2">
                        <p className="font-mono text-[9px] uppercase tracking-wider text-fog">
                          {label}
                        </p>
                        <p className="mt-1 text-sm text-parchment">
                          <span className="font-semibold text-moss">{imported}</span> imported
                          {skipped > 0 && <span className="text-fog"> · {skipped} skipped</span>}
                        </p>
                      </div>
                    ))}
                  </div>
                  {restoreResult.errors.length > 0 && (
                    <div className="mt-3">
                      <p className="font-mono text-[9px] uppercase tracking-wider text-ember">
                        {restoreResult.errors.length} error
                        {restoreResult.errors.length !== 1 && "s"}
                      </p>
                      <div className="mt-1 max-h-32 overflow-y-auto rounded-lg bg-ink/50 p-2">
                        {restoreResult.errors.map((err, i) => (
                          <p key={i} className="text-xs text-fog">
                            {err}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Scheduled Backups ── */}
        <div className="rounded-[18px] border border-mist bg-slate p-5">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-lg font-medium text-parchment">
              <CalendarDays className="h-5 w-5 text-spark" />
              Scheduled Backups
            </h2>
            {isPrimary ? (
              <button
                onClick={handleRunBackup}
                disabled={runningBackup}
                className="flex items-center gap-2 rounded-xl border border-mist px-3 py-1.5 text-xs font-medium text-parchment transition-colors hover:border-spark hover:text-spark disabled:opacity-50"
              >
                {runningBackup ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Clock className="h-3 w-3" />
                )}
                {runningBackup ? "Running..." : "Run Now"}
              </button>
            ) : (
              <div className="flex items-center gap-1.5 rounded-xl border border-mist px-3 py-1.5 text-xs text-fog">
                <Shield className="h-3 w-3" />
                Primary admin only
              </div>
            )}
          </div>
          <p className="mt-1 text-sm text-fog">
            Full ZIP backups (metadata + audio) saved to R2 automatically. Weekly every Sunday,
            monthly on the first Sunday. Keeps 4 weekly and 6 monthly.
          </p>

          {backupStatus === "running" && (
            <div className="mt-3 flex items-center gap-2 rounded-xl bg-spark/10 px-4 py-3 text-sm text-spark">
              <Loader2 className="h-4 w-4 animate-spin" />
              Backup running -- packaging metadata + audio files to R2...
            </div>
          )}

          {backupStatus === "success" && (
            <div className="mt-3 flex items-center gap-2 rounded-xl border border-moss/30 bg-moss/5 px-4 py-3 text-sm text-moss">
              <CheckCircle2 className="h-4 w-4" />
              Backup complete -- full ZIP saved to R2.
            </div>
          )}

          {backupStatus === "error" && (
            <div className="mt-3 flex items-center gap-2 rounded-xl bg-ember/10 px-4 py-3 text-sm text-ember">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {backupError || "Backup failed"}
            </div>
          )}

          <div className="mt-4">
            {loadingFiles ? (
              <div className="flex items-center gap-2 py-4 text-sm text-fog">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading backups...
              </div>
            ) : backupFiles.length === 0 ? (
              <p className="py-4 text-sm text-fog">No scheduled backups yet. Click Run Now or wait for the next Sunday.</p>
            ) : (
              <div className="space-y-4">
                {/* Weekly */}
                {backupFiles.filter((f) => f.tier === "weekly").length > 0 && (
                  <div>
                    <h3 className="mb-1.5 font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
                      Weekly
                    </h3>
                    <div className="space-y-1">
                      {backupFiles
                        .filter((f) => f.tier === "weekly")
                        .map((file) => (
                          <div
                            key={file.key}
                            className="flex items-center justify-between rounded-xl bg-slate-raised px-3 py-2"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <FileArchive className="h-3.5 w-3.5 shrink-0 text-spark" />
                              <span className="truncate text-sm text-parchment">
                                {new Date(file.uploaded).toLocaleDateString("en-US", {
                                  weekday: "short",
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })}
                              </span>
                              <span className="shrink-0 text-xs text-fog">{formatBytes(file.size)}</span>
                              {file.sound_count && (
                                <span className="hidden shrink-0 text-xs text-fog sm:inline">
                                  {file.sound_count} sounds
                                </span>
                              )}
                            </div>
                            <button
                              onClick={() => handleDownloadBackup(file)}
                              disabled={downloading === file.key}
                              className="flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-xs text-fog transition-colors hover:bg-ink hover:text-spark disabled:opacity-50"
                            >
                              {downloading === file.key ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Download className="h-3 w-3" />
                              )}
                              <span className="hidden sm:inline">Download</span>
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Monthly */}
                {backupFiles.filter((f) => f.tier === "monthly").length > 0 && (
                  <div>
                    <h3 className="mb-1.5 font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
                      Monthly
                    </h3>
                    <div className="space-y-1">
                      {backupFiles
                        .filter((f) => f.tier === "monthly")
                        .map((file) => (
                          <div
                            key={file.key}
                            className="flex items-center justify-between rounded-xl bg-slate-raised px-3 py-2"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <FileArchive className="h-3.5 w-3.5 shrink-0 text-spark" />
                              <span className="truncate text-sm text-parchment">
                                {new Date(file.uploaded).toLocaleDateString("en-US", {
                                  weekday: "short",
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })}
                              </span>
                              <span className="shrink-0 text-xs text-fog">{formatBytes(file.size)}</span>
                              {file.sound_count && (
                                <span className="hidden shrink-0 text-xs text-fog sm:inline">
                                  {file.sound_count} sounds
                                </span>
                              )}
                            </div>
                            <button
                              onClick={() => handleDownloadBackup(file)}
                              disabled={downloading === file.key}
                              className="flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-xs text-fog transition-colors hover:bg-ink hover:text-spark disabled:opacity-50"
                            >
                              {downloading === file.key ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Download className="h-3 w-3" />
                              )}
                              <span className="hidden sm:inline">Download</span>
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
